import { ColDef, ValueFormatterParams } from '@ag-grid-community/core'
import { currentTimezoneConverter } from '@helios/utils'
import { NfColumnTypes, NfGridOptions } from '@northfork/ag-grid'
import {
  GridOptionsAngular,
  isEmptyFilter,
  NgGridComponents,
} from '@northfork/ag-grid-angular'
import {
  DisplayDateTimeFormat,
  ISODateTimeFormat,
  formatDate,
} from '@northfork/basics'
import { heliosDefaultColWidth } from './default'
import { timeZone } from './time'

/** DateTimeFormatter for displaying date-time objects as date timestamps */
export const dateTimeFormatter = (params: ValueFormatterParams) => {
  const date = formatDate(
    params.value,
    DisplayDateTimeFormat,
    ISODateTimeFormat,
  )

  return date ? `${date} ${timeZone}` : ''
}
export const shortDateTimeFormatter = (params: ValueFormatterParams) => {
  const DisplayShortDateTimeFormat = 'MM/dd/yy hh:mm a'
  const date = formatDate(
    params.value,
    DisplayShortDateTimeFormat,
    ISODateTimeFormat,
  )
  return date ? `${date} ${timeZone}` : ''
}

export const currentTimeZoneDateFormatter = (params: ValueFormatterParams) => {
  const formattedDate = currentTimezoneConverter(params.value)
  return formattedDate ? `${formattedDate} ${timeZone}` : ''
}

const NfDateTimeColDef =
  NfGridOptions.columnTypes?.[NfColumnTypes.DateTime] ?? {}

/** The parts of Northfork's datetime filter the patch at the bottom reaches into. */
interface DateTimeFilterInternals {
  filterActive(): boolean
  appliedModel?: { type?: string }
}

export const HsDateTimeColDef: ColDef = {
  ...NfDateTimeColDef,
  filter: NgGridComponents.dateTimeFilter,
  filterParams: {
    ...NfDateTimeColDef.filterParams,
    filterOptions: [
      ...NfDateTimeColDef.filterParams.filterOptions,
      'blank',
      'notBlank',
    ],
  },
  valueFormatter: dateTimeFormatter,
  width: heliosDefaultColWidth.DateTime,
}
export const HsShortDateTimeColDef: ColDef = {
  ...NfDateTimeColDef,
  filter: NgGridComponents.dateTimeFilter,
  filterParams: {
    ...NfDateTimeColDef.filterParams,
    filterOptions: [
      ...NfDateTimeColDef.filterParams.filterOptions,
      'blank',
      'notBlank',
    ],
  },
  valueFormatter: shortDateTimeFormatter,
  width: heliosDefaultColWidth.DateTime,
}
export const HsCurrentTimeZoneDateColDef: ColDef = {
  ...NfDateTimeColDef,
  filter: NgGridComponents.dateTimeFilter,
  filterParams: {
    ...NfDateTimeColDef.filterParams,
    filterOptions: [
      ...NfDateTimeColDef.filterParams.filterOptions,
      'blank',
      'notBlank',
    ],
  },
  valueFormatter: currentTimeZoneDateFormatter,
  width: heliosDefaultColWidth.DateTime,
}

/**
 * Super nasty bug in Northfork which this method is not defined
 * and they have a focus trap on the date time filter. This
 * prevents that bug from occuring.
 *
 * TODO: Remove once fixed in northfork.
 */
GridOptionsAngular.frameworkComponents[
  NgGridComponents.dateTimeFilter
].prototype.hidePopupMethod = () => {}

/**
 * Northfork's scaler filter reports a filter as inactive unless it holds a
 * non-empty value, so blank/notBlank — which carry no value by design — never
 * produce a model and the option silently does nothing. Their text filter has
 * the isEmptyFilter escape hatch this one is missing.
 *
 * Shadowing filterActive here rather than on the shared base scopes the fix to
 * the datetime filter, which is the only one offering these options above.
 *
 * TODO: Remove once fixed in northfork.
 */
const dateTimeFilterPrototype = GridOptionsAngular.frameworkComponents[
  NgGridComponents.dateTimeFilter
].prototype as DateTimeFilterInternals

const northforkFilterActive = dateTimeFilterPrototype.filterActive

dateTimeFilterPrototype.filterActive = function (
  this: DateTimeFilterInternals,
) {
  const type = this.appliedModel?.type

  return type != null && isEmptyFilter(type)
    ? true
    : northforkFilterActive.call(this)
}
